/**
 * ScriptsEditor - Manage custom scripts for funnel steps
 *
 * Allows adding, editing, enabling/disabling scripts with injection position control.
 * Scripts are stored in stepConfig.scripts as an array of ScriptConfig objects.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Trash2,
  Code2,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  BookOpen,
  Sparkles,
  Maximize2,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CodeEditor, CodeViewer } from '@/components/ui/code-editor';
import { allExamples } from '@/data/script-injection-examples';
import {
  findMetaScriptConflicts,
  type ManagedMetaPixel,
  type MetaScriptConflict,
} from '@/lib/tracking/meta-script-conflict';
import { TRACKING_CONFIGS } from '../constants/tracking';
import { TrackingType } from '../types/tracking';
import type { ScriptConfig } from '../types';
import { TAGADAPAY_PLATFORM_NAME } from '@/lib/branding/platform-name';

interface ScriptsEditorProps {
  scripts: ScriptConfig[];
  onChange: (scripts: ScriptConfig[]) => void;
  disabled?: boolean;
  /** Enabled Meta pixels of the SAME step, to flag scripts that re-fire their events. */
  managedMetaPixels?: ManagedMetaPixel[];
}

const POSITION_LABELS: Record<string, string> = {
  'head-start': 'Head Start',
  'head-end': 'Head End',
  'body-start': 'Body Start',
  'body-end': 'Body End',
};

const POSITION_DESCRIPTIONS: Record<string, string> = {
  'head-start': 'Injected at the very start of <head>',
  'head-end': 'Injected at the end of <head> (recommended)',
  'body-start': 'Injected at the start of <body>',
  'body-end': 'Injected at the end of <body>',
};

/**
 * Fullscreen script editor dialog
 */
function ScriptEditorDialog({
  script,
  isOpen,
  onClose,
  onUpdate,
  disabled,
}: {
  script: ScriptConfig | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updates: Partial<ScriptConfig>) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('storefront');
  const [localScript, setLocalScript] = React.useState<ScriptConfig | null>(null);
  const [copied, setCopied] = React.useState(false);

  // Sync local state when dialog opens
  React.useEffect(() => {
    if (isOpen && script) {
      setLocalScript({ ...script });
    }
  }, [isOpen, script]);

  const handleSave = () => {
    if (localScript) {
      onUpdate(localScript);
      onClose();
    }
  };

  const handleCopy = async () => {
    if (localScript) {
      await navigator.clipboard.writeText(localScript.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!localScript) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[92dvh] w-full max-w-[calc(100%-1rem)] flex-col p-0 sm:h-[90vh] sm:max-w-[95vw] md:max-w-5xl lg:max-w-6xl [&>button:last-child]:hidden">
        <DialogHeader className="flex-shrink-0 !gap-0 border-b px-4 py-2">
          <div className="flex items-center justify-between py-0.5">
            <div className="flex items-center gap-3">
              <Code2 className="h-5 w-5 text-emerald-500" />
              <DialogTitle className="text-lg">{t('scriptsEditor.editScript')}</DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={handleCopy}>
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('scriptsEditor.copied') : t('scriptsEditor.copy')}
              </Button>
              <Button variant="outline" size="sm" className="h-8" onClick={onClose}>
                {t('scriptsEditor.cancel')}
              </Button>
              <Button size="sm" className="h-8" onClick={handleSave} disabled={disabled}>
                {t('scriptsEditor.saveChanges')}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden px-5 pb-4 pt-3">
          {/* Top row: Name, Position, Enabled */}
          <div className="flex flex-shrink-0 items-end gap-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">{t('scriptsEditor.scriptName')}</label>
              <Input
                value={localScript.name}
                onChange={(e) => setLocalScript({ ...localScript, name: e.target.value })}
                placeholder={t('scriptsEditor.scriptNamePlaceholder')}
                disabled={disabled}
                className="h-9"
              />
            </div>
            <div className="w-40 shrink-0 space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">{t('scriptsEditor.position')}</label>
              <Select
                value={localScript.position || 'head-end'}
                onValueChange={(position) =>
                  setLocalScript({ ...localScript, position: position as ScriptConfig['position'] })
                }
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(POSITION_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex h-9 shrink-0 items-center gap-2 pl-1">
              <Switch
                checked={localScript.enabled}
                onCheckedChange={(enabled) => setLocalScript({ ...localScript, enabled })}
                disabled={disabled}
              />
              <span className="text-muted-foreground text-sm">{t('scriptsEditor.enabled')}</span>
            </div>
          </div>

          {/* Code editor - takes remaining space */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <CodeEditor
              value={localScript.content}
              onChange={(value) => setLocalScript({ ...localScript, content: value })}
              height="calc(90vh - 200px)"
              disabled={disabled}
              placeholder="// Enter your script code here..."
              className="h-full"
            />
          </div>

          {/* Help text */}
          <div className="flex-shrink-0 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 dark:border-primary/30 dark:bg-primary/10">
            <p className="text-xs text-primary dark:text-primary">
              <strong>Tip:</strong> Use{' '}
              <code className="rounded bg-primary/15 px-1 dark:bg-primary/20">Tagada.ready()</code> to wait for
              DOM,
              <code className="ml-1 rounded bg-primary/15 px-1 dark:bg-primary/20">Tagada.order</code> for order
              data (thank you page),
              <code className="ml-1 rounded bg-primary/15 px-1 dark:bg-primary/20">Tagada.checkout</code> for
              checkout data.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Word the conflict for a merchant: name the section they must open, and show the
 * event labels the pixel form uses rather than Meta's raw identifiers.
 */
function describeMetaConflict(
  t: (key: string, options?: Record<string, unknown>) => string,
  conflict: MetaScriptConflict,
): string {
  const section = t('stepEditor.clientSidePixels');
  const pixelIds = conflict.duplicatedPixelIds.join(', ');

  if (conflict.duplicatedEvents.length === 0) {
    return t('scriptsEditor.metaDuplicatePixel', { pixelIds, section });
  }

  const labels = TRACKING_CONFIGS[TrackingType.FACEBOOK].events ?? [];
  const events = conflict.duplicatedEvents
    .map((event) => labels.find((e) => e.key === event)?.label ?? event)
    .join(', ');

  // The pixel id is what tells a merchant running several which entry to open.
  return pixelIds
    ? t('scriptsEditor.metaDuplicateEvents', { events, pixelIds, section })
    : t('scriptsEditor.metaDuplicateEventsNoPixel', { events, section });
}

/**
 * Single script item editor
 */
function ScriptItem({
  script,
  index,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onExpand,
  isFirst,
  isLast,
  disabled,
  defaultOpen,
  metaConflict,
}: {
  script: ScriptConfig & { id?: string };
  index: number;
  onUpdate: (updates: Partial<ScriptConfig>) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onExpand: () => void;
  isFirst: boolean;
  isLast: boolean;
  disabled?: boolean;
  defaultOpen?: boolean;
  metaConflict?: MetaScriptConflict | null;
}) {
  const { t } = useTranslation('storefront');
  const [isOpen, setIsOpen] = React.useState(defaultOpen ?? false);
  const [copied, setCopied] = React.useState(false);
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (defaultOpen) {
      // Wait for collapsible content to render then focus + select
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = async () => {
    await navigator.clipboard.writeText(script.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lineCount = script.content.split('\n').length;

  return (
    <Card className={cn('overflow-hidden py-0 transition-all', !script.enabled && 'opacity-60')}>
      {metaConflict && (
        <div
          role="alert"
          className="flex items-start gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20"
        >
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-300"
          />
          <div className="text-xs text-amber-900 dark:text-amber-100">
            <p className="font-medium">
              {t('scriptsEditor.metaDuplicateTitle')}
              {script.name ? ` — ${script.name}` : ''}
            </p>
            <p className="mt-0.5">{describeMetaConflict(t, metaConflict)}</p>
          </div>
        </div>
      )}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Header - Always visible */}
        <div className="flex items-center gap-2 px-2 py-1.5">
          {/* Move buttons */}
          <div className="flex flex-col">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-4 w-4"
              onClick={onMoveUp}
              disabled={isFirst || disabled}
            >
              <ChevronUp className="h-2.5 w-2.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-4 w-4"
              onClick={onMoveDown}
              disabled={isLast || disabled}
            >
              <ChevronDown className="h-2.5 w-2.5" />
            </Button>
          </div>

          {/* Enable/Disable toggle */}
          <Switch
            checked={script.enabled}
            onCheckedChange={(enabled) => onUpdate({ enabled })}
            disabled={disabled}
            className="scale-75"
          />

          {/* Script info */}
          <CollapsibleTrigger asChild>
            <button className="flex flex-1 items-center gap-2 text-left" disabled={disabled}>
              <Code2
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  script.enabled ? 'text-emerald-500' : 'text-muted-foreground',
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{script.name || t('scriptsEditor.unnamedScript')}</span>
                  <Badge variant="secondary" className="h-4 shrink-0 px-1 py-0 text-[9px]">
                    {POSITION_LABELS[script.position || 'head-end']}
                  </Badge>
                  <span className="text-muted-foreground text-[10px]">
                    ({lineCount} line{lineCount !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>
              <ChevronDown
                className={cn(
                  'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                  isOpen && 'rotate-180',
                )}
              />
            </button>
          </CollapsibleTrigger>

          {/* Expand button */}
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground h-6 w-6 shrink-0"
            onClick={onExpand}
            disabled={disabled}
            title={t('scriptsEditor.openFullscreen')}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>

          {/* Delete button */}
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive h-6 w-6 shrink-0"
            onClick={onDelete}
            disabled={disabled}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Expanded content */}
        <CollapsibleContent>
          <div className="space-y-3 border-t bg-slate-50/50 px-3 py-2.5 dark:bg-slate-900/50">
            {/* Name and Position in a row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-muted-foreground text-[10px] font-medium">{t('scriptsEditor.scriptName')}</label>
                <Input
                  ref={nameInputRef}
                  value={script.name}
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  placeholder={t('scriptsEditor.scriptNamePlaceholder')}
                  disabled={disabled}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-muted-foreground text-[10px] font-medium">{t('scriptsEditor.position')}</label>
                <Select
                  value={script.position || 'head-end'}
                  onValueChange={(position) => onUpdate({ position: position as ScriptConfig['position'] })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(POSITION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value} className="text-xs">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Script content */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-muted-foreground text-[10px] font-medium">{t('scriptsEditor.scriptCode')}</label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 text-[10px]"
                  onClick={handleCopy}
                  disabled={disabled}
                >
                  {copied ? (
                    <>
                      <Check className="mr-1 h-2.5 w-2.5" />
                      {t('scriptsEditor.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1 h-2.5 w-2.5" />
                      {t('scriptsEditor.copy')}
                    </>
                  )}
                </Button>
              </div>
              <CodeEditor
                value={script.content}
                onChange={(value) => onUpdate({ content: value })}
                height="120px"
                disabled={disabled}
                placeholder="// Your code here"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/**
 * Examples panel with template scripts
 */
function ExamplesPanel({ onUseExample }: { onUseExample: (name: string, code: string) => void }) {
  const { t } = useTranslation('storefront');
  const [activeExample, setActiveExample] = React.useState<keyof typeof allExamples>('basic');
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const example = allExamples[activeExample];
    await navigator.clipboard.writeText(example.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseExample = () => {
    const example = allExamples[activeExample];
    // Extract a clean name from the title (remove emoji)
    const cleanName = example.title.replace(/^[^\w]+/, '').trim();
    onUseExample(cleanName, example.code);
  };

  return (
    <div className="space-y-3">
      {/* Example tabs */}
      <div className="flex flex-wrap gap-1">
        {Object.entries(allExamples).map(([key, example]) => (
          <button
            key={key}
            onClick={() => setActiveExample(key as keyof typeof allExamples)}
            className={cn(
              'rounded-full px-2.5 py-1 text-[11px] transition-colors',
              activeExample === key
                ? 'border border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-700 dark:bg-violet-900/50 dark:text-violet-300'
                : 'border border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
            )}
          >
            {example.title}
          </button>
        ))}
      </div>

      {/* Code display */}
      <div className="overflow-hidden rounded-lg border dark:border-slate-700">
        <div className="flex items-center justify-between border-b bg-slate-100 px-3 py-1.5 dark:border-slate-700 dark:bg-slate-800">
          <span className="text-muted-foreground text-xs font-medium">
            {allExamples[activeExample]?.title}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={handleCopy}>
              {copied ? (
                <>
                  <Check className="mr-1 h-3 w-3" />
                  {t('scriptsEditor.copied')}
                </>
              ) : (
                <>
                  <Copy className="mr-1 h-3 w-3" />
                  {t('scriptsEditor.copy')}
                </>
              )}
            </Button>
            <Button size="sm" className="h-6 px-2 text-xs" onClick={handleUseExample}>
              <Sparkles className="mr-1 h-3 w-3" />
              {t('scriptsEditor.useExample')}
            </Button>
          </div>
        </div>
        <CodeViewer value={allExamples[activeExample]?.code || ''} height="200px" theme="dark" />
      </div>
    </div>
  );
}

/**
 * Tagada helpers documentation panel
 */
function TagadaHelpersPanel() {
  return (
    <div className="space-y-4 text-sm">
      {/* Quick decision guide */}
      <div className="rounded-md border border-violet-200 bg-violet-50/50 p-3 dark:border-violet-800 dark:bg-violet-950/30">
        <h5 className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-violet-800 dark:text-violet-200">
          <span>🧭</span> When to use what
        </h5>
        <ul className="space-y-1 text-[11px] text-violet-900/80 dark:text-violet-100/80">
          <li>
            <strong>DOM manipulation only</strong> →{' '}
            <code className="rounded bg-violet-100 px-1 dark:bg-violet-900">Tagada.ready(cb)</code>
          </li>
          <li>
            <strong>Snapshot of funnel / checkout / order data</strong> →{' '}
            <code className="rounded bg-violet-100 px-1 dark:bg-violet-900">Tagada.loaded(cb)</code>{' '}
            (fires once data is populated)
          </li>
          <li>
            <strong>Live data tracking</strong> (items change, promo applied, shipping updates) →{' '}
            <code className="rounded bg-violet-100 px-1 dark:bg-violet-900">Tagada.on*Update(cb)</code>
          </li>
          <li>
            <strong>Wait for a specific DOM element</strong> →{' '}
            <code className="rounded bg-violet-100 px-1 dark:bg-violet-900">Tagada.waitForElement(sel, cb)</code>
          </li>
        </ul>
      </div>

      {/* Timing helpers */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 font-medium text-violet-800 dark:text-violet-200">
          <span>⏱️</span> Timing Helpers
        </h5>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.ready(cb)
            </code>
            <span className="text-muted-foreground">
              Fires when the DOM is ready. <strong>Funnel data may still be null.</strong> Use for plain DOM work.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.loaded(cb)
            </code>
            <span className="text-muted-foreground">
              Fires when DOM ready <em>and</em> funnel initialized. <strong>Use this to read <code>Tagada.funnel</code>, <code>Tagada.ressources</code>, <code>Tagada.checkout</code>, <code>Tagada.order</code>.</strong>
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.delay(cb, ms)
            </code>
            <span className="text-muted-foreground">Fires after the given ms.</span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.waitForElement(sel, cb)
            </code>
            <span className="text-muted-foreground">Resolves once a matching element exists in the DOM (MutationObserver).</span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.waitForElements(sel, cb)
            </code>
            <span className="text-muted-foreground">Same as above but for <code>querySelectorAll</code>.</span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.retry(cond, cb)
            </code>
            <span className="text-muted-foreground">Polls a predicate up to 10 times; runs <code>cb</code> when true or attempts exhausted.</span>
          </div>
        </div>
      </div>

      {/* Available data */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 font-medium text-violet-800 dark:text-violet-200">
          <span>📊</span> Available Data
        </h5>
        <p className="mb-2 text-[10px] text-muted-foreground">
          Always wrap access in <code>Tagada.loaded()</code> on first load, or subscribe via a listener — these values are <strong>null until the funnel and API responses settle</strong>.
        </p>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.pageType
            </code>
            <span className="text-muted-foreground">
              Current step id, e.g. <code>'step_xxx'</code>. Set after funnel init.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.funnel
            </code>
            <span className="text-muted-foreground">
              <code>{'{ sessionId, funnelId, currentStepId, previousStepId, ressources }'}</code>.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.ressources
            </code>
            <span className="text-muted-foreground">
              Raw funnel-context map (<code>checkoutToken</code>, <code>checkoutSession</code>, <code>customer</code>, <code>order</code>, <code>payment</code>, …). Note the French spelling — kept for V1 compat.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.checkout.session
            </code>
            <span className="text-muted-foreground">
              Full checkout session (36 keys: <code>id</code>, <code>customer</code>, <code>shippingAddress</code>, <code>billingAddress</code>, <code>sessionLineItems</code>, <code>orderSummaries</code>, <code>shippingRate</code>, <code>store</code>, …). Populated on the <strong>checkout</strong> page after the API settles.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.checkout.orderSummary
            </code>
            <span className="text-muted-foreground">
              Live totals (<code>items</code>, <code>subtotalAmount</code>, <code>totalAdjustedAmount</code>, <code>totalTaxAmount</code>, <code>shippingCost</code>, <code>adjustments</code>, <code>currency</code>, …). Changes whenever cart/promo/shipping changes — subscribe for live tracking.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.order
            </code>
            <span className="text-muted-foreground">
              Full order data on the <strong>thank-you</strong> page (<code>id</code>, <code>paidAmount</code>, <code>currency</code>, <code>status</code>, <code>customer</code>, <code>items</code>, …). Null on other pages.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.stepConfig
            </code>
            <span className="text-muted-foreground">
              Runtime step config (pixels, scripts, selected A/B variant) — reflects what the CRM configured for this step.
            </span>
          </div>
        </div>
      </div>

      {/* Reactive listeners */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 font-medium text-violet-800 dark:text-violet-200">
          <span>🔔</span> Reactive Listeners
        </h5>
        <p className="mb-2 text-[10px] text-muted-foreground">
          All listeners return an unsubscribe function. Use them for analytics on cart/total changes, loyalty flows that react to customer fields, etc.
        </p>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.onCheckoutSessionUpdate(cb)
            </code>
            <span className="text-muted-foreground">
              Fires on any checkout-session change (customer email, shipping address, line items, promotions applied, etc). Payload: <code>{'{ checkoutSession }'}</code>.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.onOrderSummaryUpdate(cb)
            </code>
            <span className="text-muted-foreground">
              Fires on total/shipping/promo/tax recomputation. Payload: <code>{'{ orderSummary }'}</code>.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <code className="shrink-0 rounded bg-violet-100 px-1 font-mono dark:bg-violet-900">
              Tagada.onOrderUpdate(cb)
            </code>
            <span className="text-muted-foreground">
              Fires once the order resource is fetched on the thank-you page. Payload: <code>{'{ order }'}</code>.
            </span>
          </div>
        </div>
      </div>

      {/* Pages */}
      <div>
        <h5 className="mb-2 flex items-center gap-2 font-medium text-violet-800 dark:text-violet-200">
          <span>📄</span> Data availability by page
        </h5>
        <div className="overflow-hidden rounded-md border border-violet-200 text-[11px] dark:border-violet-800">
          <table className="w-full">
            <thead className="bg-violet-50 dark:bg-violet-950/50">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Field</th>
                <th className="px-2 py-1 text-center font-medium">Landing</th>
                <th className="px-2 py-1 text-center font-medium">Checkout</th>
                <th className="px-2 py-1 text-center font-medium">Offer</th>
                <th className="px-2 py-1 text-center font-medium">Thank-you</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-t border-violet-200 dark:border-violet-800">
                <td className="px-2 py-1"><code>funnel</code>, <code>pageType</code>, <code>stepConfig</code></td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">✅</td>
              </tr>
              <tr className="border-t border-violet-200 dark:border-violet-800">
                <td className="px-2 py-1"><code>checkout.session</code></td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">✅</td>
              </tr>
              <tr className="border-t border-violet-200 dark:border-violet-800">
                <td className="px-2 py-1"><code>checkout.orderSummary</code></td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">✅</td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">—</td>
              </tr>
              <tr className="border-t border-violet-200 dark:border-violet-800">
                <td className="px-2 py-1"><code>order</code></td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">—</td>
                <td className="px-2 py-1 text-center">✅</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Main ScriptsEditor component
 */
export function ScriptsEditor({
  scripts,
  onChange,
  disabled,
  managedMetaPixels = [],
}: ScriptsEditorProps) {
  const { t } = useTranslation('storefront');
  const [showExamples, setShowExamples] = React.useState(false);
  const [showHelpers, setShowHelpers] = React.useState(false);
  const [editingScriptIndex, setEditingScriptIndex] = React.useState<number | null>(null);
  const [newlyAddedIndex, setNewlyAddedIndex] = React.useState<number | null>(null);

  // Clear newlyAddedIndex after it's been consumed
  React.useEffect(() => {
    if (newlyAddedIndex !== null) {
      const timer = setTimeout(() => setNewlyAddedIndex(null), 100);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedIndex]);

  // Add internal IDs for React keys if not present
  const scriptsWithIds = React.useMemo(
    () => scripts.map((s, i) => ({ ...s, id: (s as any).id || `temp_${i}` })),
    [scripts],
  );

  const metaConflicts = React.useMemo(
    () => findMetaScriptConflicts(scripts, managedMetaPixels),
    [scripts, managedMetaPixels],
  );

  const handleAdd = () => {
    const newScript: ScriptConfig = {
      name: `Script ${scripts.length + 1}`,
      enabled: true,
      content: '',
      position: 'head-end',
    };
    setNewlyAddedIndex(scripts.length);
    onChange([...scripts, newScript]);
  };

  const handleAddFromExample = (name: string, code: string) => {
    const newScript: ScriptConfig = {
      name,
      enabled: true,
      content: code,
      position: 'head-end',
    };
    setNewlyAddedIndex(scripts.length);
    onChange([...scripts, newScript]);
    setShowExamples(false); // Close examples panel after adding
  };

  const handleUpdate = (index: number, updates: Partial<ScriptConfig>) => {
    const updated = [...scripts];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleDelete = (index: number) => {
    onChange(scripts.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...scripts];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === scripts.length - 1) return;
    const updated = [...scripts];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {/* Quick actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2',
            showHelpers && 'border-violet-300 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/30',
          )}
          onClick={() => {
            setShowHelpers(!showHelpers);
            if (!showHelpers) setShowExamples(false);
          }}
        >
          <BookOpen className="h-3.5 w-3.5" />
          {TAGADAPAY_PLATFORM_NAME} Helpers
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2',
            showExamples && 'border-violet-300 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/30',
          )}
          onClick={() => {
            setShowExamples(!showExamples);
            if (!showExamples) setShowHelpers(false);
          }}
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('scriptsEditor.examples')}
        </Button>
      </div>

      {/* Helpers panel */}
      {showHelpers && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-900/20">
          <TagadaHelpersPanel />
        </div>
      )}

      {/* Examples panel */}
      {showExamples && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-900/20">
          <ExamplesPanel onUseExample={handleAddFromExample} />
        </div>
      )}

      {/* Scripts list */}
      {scriptsWithIds.length > 0 ? (
        <div className="space-y-2">
          {scriptsWithIds.map((script, index) => (
            <ScriptItem
              key={script.id}
              script={script}
              index={index}
              onUpdate={(updates) => handleUpdate(index, updates)}
              onDelete={() => handleDelete(index)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              onExpand={() => setEditingScriptIndex(index)}
              isFirst={index === 0}
              isLast={index === scriptsWithIds.length - 1}
              disabled={disabled}
              defaultOpen={index === newlyAddedIndex}
              metaConflict={metaConflicts[index]}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Code2 className="text-muted-foreground/50 mx-auto h-8 w-8" />
          <p className="text-muted-foreground mt-2 text-sm">{t('scriptsEditor.noScriptsConfigured')}</p>
          <p className="text-muted-foreground/70 text-xs">
            {t('scriptsEditor.addCustomScriptsDescription')}
          </p>
          <Button
            variant="link"
            size="sm"
            className="mt-2 text-violet-600 dark:text-violet-400"
            onClick={() => setShowExamples(true)}
          >
            {t('scriptsEditor.browseExamples')}
          </Button>
        </div>
      )}

      {/* Add button */}
      <Button variant="outline" size="sm" className="w-full" onClick={handleAdd} disabled={disabled}>
        <Plus className="mr-2 h-4 w-4" />
        {t('scriptsEditor.addScript')}
      </Button>

      {/* Info */}
      {scripts.length > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 dark:border-primary/30 dark:bg-primary/10">
          <p className="text-xs text-primary dark:text-primary">
            <strong>{t('scriptsEditor.note')}:</strong> {t('scriptsEditor.onlyEnabledScripts')}
          </p>
        </div>
      )}

      {/* Fullscreen editor dialog */}
      <ScriptEditorDialog
        script={editingScriptIndex !== null ? scripts[editingScriptIndex] : null}
        isOpen={editingScriptIndex !== null}
        onClose={() => setEditingScriptIndex(null)}
        onUpdate={(updates) => {
          if (editingScriptIndex !== null) {
            handleUpdate(editingScriptIndex, updates);
          }
        }}
        disabled={disabled}
      />
    </div>
  );
}
